import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { SearchspringRecommendProvider } from 'libs/searchspring/fetchers/recommend'
import { CarouselResults } from 'libs/searchspring/components/recommendations/CarouselResults'
import clsx from 'clsx'
import { BeaconAnalyticsProvider } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { parseJSON } from '@/components/helpers/utils'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'

// Helper component to track results state
const CarouselResultsWrapper = ({ profile, onResultsChange, children }) => {
  const { results, isLoading } = useSearchspringContext()

  // Notify parent when results/loading state changes
  useEffect(() => {
    onResultsChange(profile, {
      hasResults: results?.results?.length > 0,
      isLoading
    })
  }, [results, isLoading, profile, onResultsChange])

  return <>{children}</>
}

export function CarouselTabbed({ values, itemClass, productId, carouselOptions = {}, productCardOptions, title, limit }) {
  const profiles = useMemo(() => values ? parseJSON(values) : [], [values])
  const parsedOptions = parseJSON(carouselOptions)
  // Handle groupCells value conversion
  if (parsedOptions?.groupCells !== undefined) {
    const value = parsedOptions.groupCells

    // Convert string 'true'/'false' to boolean
    if (value === 'true') {
      parsedOptions.groupCells = true
    } else if (value === 'false') {
      parsedOptions.groupCells = false
    // Convert numeric string to number
    } else if (!isNaN(value) && typeof value === 'string') {
      parsedOptions.groupCells = Number(value)
    }
  }

  const parsedProductCardOptions = parseJSON(productCardOptions)
  const cardOptions = productCardOptions && Object.keys(parsedProductCardOptions).length > 0
    ? { productCardOptions: parsedProductCardOptions }
    : {}

  const backgroundColour = parsedProductCardOptions.transparentCards
    ? 'bg-[--section-background-color,theme(colors.brand.white)]'
    : 'bg-brand-white [--section-background-color:theme(colors.brand.white)]'

  const [activeProfile, setActiveProfile] = useState(profiles[0]?.profile)
  const [profileStates, setProfileStates] = useState({})

  // Callback to update profile states
  const handleResultsChange = useCallback((profile, state) => {
    setProfileStates(prev => ({
      ...prev,
      [profile]: state
    }))
  }, [])

  // Filter out profiles with no results
  const visibleProfiles = profiles?.filter(profile => {
    const state = profileStates[profile.profile]
    return !state || state.isLoading || state.hasResults
  })

  return (
    <div className="flex flex-col gap-2 md:gap-4">
      <div className="flex items-center justify-between px-2 overflow-x-auto overflow-y-hidden lg:px-4 md:justify-start">
        {title}
        <div className="flex items-center overflow-x-auto whitespace-nowrap">
          {visibleProfiles.map((item, i) => (
            <button
              key={item.profile}
              data-dl-section-id={item.profile}
              data-dl-section-name={item.label}
              data-dl-tab-label={item.label}
              className={clsx(
                'paragraph-md pb-1 btn btn-md btn-primary-text link after:mx-3 h-fit md-max:last-of-type:pr-0 md-max:last-of-type:after:mr-0',
                item.profile === activeProfile
                  ? 'show-wavy-underline after:[--svg-color:theme(colors.brand.blue)]'
                  : '',
                (i === 0 && title) && 'pl-0'
              )}
              onClick={() => setActiveProfile(item.profile)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div className="relative grid grid-cols-1 grid-rows-1">
        {profiles.map((item) => (
          <SearchspringRecommendProvider
            key={item.profile}
            profile={item.profile}
            productId={productId}
            limit={limit}
          >
            <BeaconAnalyticsProvider profile={item.profile}>
              <CarouselResultsWrapper
                profile={item.profile}
                onResultsChange={handleResultsChange}
              >
                <CarouselResults
                  className={itemClass}
                  options={parsedOptions}
                  wrapperClassName={clsx(
                    'transition-opacity duration-150 row-start-1 col-start-1',
                    item.profile !== activeProfile
                      ? 'pointer-events-none touch-none opacity-0 delay-150'
                      : 'duration-300 z-1 opacity-100',
                    backgroundColour
                  )}
                  {...cardOptions}
                />
              </CarouselResultsWrapper>
            </BeaconAnalyticsProvider>
          </SearchspringRecommendProvider>
        ))}
      </div>
    </div>
  )
}
